import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { QpxButton, QpxIcon, QpxTooltip } from '../../directives';
import { ErrorState, LoadingState } from '../data-states';
import { DocumentPreview } from './document-preview';
import { ImagePreview } from './image-preview';
import {
    FILE_PREVIEW_TYPE,
    FILE_PREVIEW_PANEL_SIDE,
    type FilePreviewAction,
    type FilePreviewInfoPanelConfig,
    type FilePreviewPanelConfig,
} from './models';
import { PdfPreview } from './pdf-preview';
import {
    PreviewActionButtons,
    PreviewPanel,
    PreviewPanelButtons,
    PreviewPanelState,
    resolvePreviewPanelDomId,
} from './preview-controls';
import { PreviewRendererLoadError } from './preview-renderer-load-error';
import { UiState, type FilePreviewStateInput } from './ui-state';

function normalizeInitialPage(value: number | undefined): number {
    if (value == null) return 1;

    const numericPage = Number(value);
    if (Number.isNaN(numericPage) || numericPage < 1) return 1;

    return Math.floor(numericPage);
}

function normalizeSearchTerm(value: string | undefined): string {
    return value?.trim() ?? '';
}

@Component({
    selector: 'qpx-file-preview',
    imports: [
        MatButtonModule,
        QpxTooltip,
        NgIcon,
        QpxButton,
        QpxIcon,
        DocumentPreview,
        ErrorState,
        ImagePreview,
        LoadingState,
        PdfPreview,
        PreviewActionButtons,
        PreviewPanel,
        PreviewPanelButtons,
        PreviewRendererLoadError,
    ],
    templateUrl: './file-preview.html',
    styleUrl: './file-preview.scss',
    providers: [UiState, PreviewPanelState],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'relative block h-full min-h-0 overflow-hidden',
    },
})
export class FilePreview {
    //#region Dependency injection
    private readonly uiState = inject(UiState);
    protected readonly panelState = inject(PreviewPanelState);
    //#endregion

    //#region Inputs
    public readonly previewUrl = input<string | null | undefined>(undefined);
    public readonly fileName = input.required<string>();
    public readonly initialPage = input(1, {
        transform: normalizeInitialPage,
    });
    public readonly searchTerm = input('', {
        transform: normalizeSearchTerm,
    });
    public readonly actions = input<readonly FilePreviewAction[]>([]);
    public readonly panels = input<readonly FilePreviewPanelConfig[]>([]);
    public readonly infoPanel = input<FilePreviewInfoPanelConfig | undefined>(
        undefined
    );
    //#endregion

    //#region Outputs
    public readonly closePreview = output<void>();
    //#endregion

    //#region Constants
    protected readonly FILE_PREVIEW_TYPE = FILE_PREVIEW_TYPE;
    //#endregion

    //#region State
    protected readonly leftPanels = computed(() =>
        this.panels().filter(
            (panel) => panel.side === FILE_PREVIEW_PANEL_SIDE.Left
        )
    );
    protected readonly rightPanels = computed(() =>
        this.panels().filter(
            (panel) => panel.side === FILE_PREVIEW_PANEL_SIDE.Right
        )
    );
    protected readonly rendererSources = computed(() => [
        {
            previewUrl: this.previewUrl(),
            fileName: this.fileName(),
        },
    ]);
    protected readonly mountedPanels = linkedSignal({
        source: () => ({
            panels: this.panels(),
            status: this.previewStatus(),
            left: this.panelState.activeLeftPanelId(),
            right: this.panelState.activeRightPanelId(),
        }),
        computation: (source, previous): readonly FilePreviewPanelConfig[] =>
            source.panels.filter((panel) => {
                if (!panel.retainOnSourceChange) return false;
                const previouslyMounted = previous?.value.some(
                    (mounted) =>
                        mounted.id === panel.id && mounted.side === panel.side
                );
                const active =
                    (panel.side === FILE_PREVIEW_PANEL_SIDE.Left
                        ? source.left
                        : source.right) === panel.id;
                return (
                    !!previouslyMounted ||
                    (active &&
                        (panel.requiresViewerReady === false ||
                            source.status === 'ready'))
                );
            }),
    });
    protected readonly errorPanels = computed(() =>
        this.panels().filter(
            (panel) =>
                !panel.retainOnSourceChange &&
                panel.requiresViewerReady === false &&
                this.isPanelActive(panel)
        )
    );
    protected readonly previewHeight = this.uiState.previewHeight;
    protected readonly previewStatus = this.uiState.previewStatus;
    protected readonly errorHeading = this.uiState.errorHeading;
    protected readonly errorDetails = this.uiState.errorDetails;
    protected readonly fileType = this.uiState.fileType;
    protected readonly errorActions = computed(() =>
        this.actions().filter((action) => !action.requiresViewerReady)
    );
    protected readonly openPreviewSourceUrl = computed<string | null>(() => {
        const previewUrl = this.previewUrl();

        if (
            this.previewStatus() !== 'error' ||
            this.fileType() !== FILE_PREVIEW_TYPE.Doc ||
            !previewUrl
        ) {
            return null;
        }

        return previewUrl;
    });
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        effect(() => {
            this.panelState.setParams({
                previewUrl: this.previewUrl(),
                fileName: this.fileName(),
                panels: this.panels(),
            });
        });
        effect(() => {
            this.uiState.setParams({
                previewUrl: this.previewUrl(),
                fileName: this.fileName(),
            });
        });
    }
    //#endregion

    //#region Template methods
    protected isPanelActive(panel: FilePreviewPanelConfig): boolean {
        return (
            (panel.side === FILE_PREVIEW_PANEL_SIDE.Left
                ? this.panelState.activeLeftPanelId()
                : this.panelState.activeRightPanelId()) === panel.id
        );
    }

    protected panelId(panel: FilePreviewPanelConfig): string {
        return resolvePreviewPanelDomId(panel.side, panel.id);
    }

    protected onViewerLoadError(
        event: Error | ErrorEvent | Event,
        source: { previewUrl: string | null | undefined; fileName: string }
    ): void {
        if (source !== this.rendererSources()[0]) return;
        this.uiState.setViewerError(event);
        this.panelState.setControlsVisible(true);
    }

    protected onViewerLoaded(source: FilePreviewStateInput): void {
        if (source !== this.rendererSources()[0]) return;
        this.uiState.setViewerLoaded();
    }

    protected onClosePreview(): void {
        this.closePreview.emit();
    }
    //#endregion
}
